// Connecticut College: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Connecticut College's Common Data Set 2025-2026
//    (Common-Data-Set-2025-26.pdf), linked from Institutional Research's
//    Conn Facts page. C1 applicants/admits/enrollees for Fall 2025 with the
//    residency table; C8 test policy for Fall 2027; C21/C22 early plans.
//    Quirks: the CDS cost section checks the box saying its costs were not
//    yet available and leaves G1/G5 blank, so the cost comes from the
//    college's 2026-2027 student budget; the CDS lists ED I closing
//    November 15, while the admission pages for fall 2027 say November 1,
//    and the page follows the admission pages.
//  - Conn Admission: First-Year Requirements & Deadlines (headed "Fall 2027
//    Admission"), Early Decision, Early Action (new for fall 2027 entry),
//    Standardized Test Policy and Optional Arts Portfolio.
//  - Conn Cost of Attendance, the 2026-2027 student budget. Conn charges one
//    comprehensive fee that covers tuition, fees, housing and food, and does
//    not split it, so the page shows it as one line.
//  - College Scorecard, school id 128902 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Connecticut College Common Data Set 2025-2026',
        url: 'https://www.conncoll.edu/institutional-research/conn-facts/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Connecticut College Admission, First-Year Requirements & Deadlines',
        url: 'https://www.conncoll.edu/admission/apply/first-year-requirements-deadlines/',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'Connecticut College Admission, Early Decision',
        url: 'https://www.conncoll.edu/admission/apply/early-decision/',
        read: '2026-10-01',
    },
    earlyAction: {
        label: 'Connecticut College Admission, Early Action',
        url: 'https://www.conncoll.edu/admission/apply/early-action/',
        read: '2026-10-01',
    },
    tests: {
        label: 'Connecticut College Admission, Standardized Test Policy',
        url: 'https://www.conncoll.edu/admission/apply/standardized-test-policy/',
        read: '2026-10-01',
    },
    arts: {
        label: 'Connecticut College Admission, Optional Arts Portfolio',
        url: 'https://www.conncoll.edu/admission/apply/optional-arts-submissions/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Connecticut College, Cost of Attendance',
        url: 'https://www.conncoll.edu/admission/tuition-fees/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?128902-Connecticut-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'connecticut-college',
    kbId: 'connecticut_college',
    name: 'Connecticut College',
    shortName: 'Conn',
    aliases: ['Conn College', 'Conn'],
    location: 'New London, CT',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Conn college colors page); decoration
    // only. The KB has no hosted logo for Conn.
    branding: {
        primary: '#002F5F',
        secondary: '#FFFFFF',
        logo_url: null,
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 7338,
        admitted: 2875,
        enrolled: 459,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 810, admitted: 301 },
            { group: 'Out-of-state', applied: 3976, admitted: 2459 },
            { group: 'International', applied: 2552, admitted: 115 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Conn\'s Common Data Set reports one first-year acceptance rate for the whole college, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early Decision I and II', applied: 546, admitted: 222 },
        ],
        note: 'The Common Data Set reports both early decision rounds together, for the fall 2025 entering class; they are part of the totals above. Conn had no early action plan that year.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Everyone applies to Conn the same way, through the Common App. One option is open to artists:',
        rows: [
            { school: 'Creative writing, dance, music, studio art and theater (optional)', requirement: 'If you plan a significant commitment to one of these, you may upload a portfolio to your applicant portal; faculty review it and give feedback to the Admission Office. It is not required for admission.' },
        ],
        note: 'Portfolios are due within 7 days of your application deadline.',
        source: 'arts',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'Mid-December', binding: false },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'Mid-February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'Late March', binding: false },
        ],
        note: 'Financial aid applicants file by November 1 for Early Decision I and Early Action, January 14 for Early Decision II and January 15 for Regular Decision. Materials that arrive after November 15 (Early Decision I and Early Action) or January 25 (Early Decision II and Regular Decision) are not guaranteed to be considered. Conn has waived its application fee.',
        source: 'deadlines',
        binding: 'Both rounds of Early Decision are binding: if admitted, you agree to enroll, pending an adequate financial aid award, and to withdraw your other applications. You, your college counselor and a parent or guardian sign the Common App Early Decision Agreement. You may apply early action or regular decision elsewhere meanwhile, but to only one college as an early decision applicant. A deferral to Regular Decision releases you from the commitment.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Connecticut College is test-optional, and if you choose not to send scores you are not at a disadvantage. If you do send SAT or ACT scores, Conn superscores the SAT and uses your highest composite ACT. Its "no harm" policy lets you consent in the Common App to having your choice changed to "no tests" if the committee finds the scores weaken your application. Scores you report yourself are accepted; enrolling students send official scores.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    currentChanges: [
        { topic: 'Early Action', line: 'Starting with fall 2027 entry, first-year applicants may apply Early Action, a nonbinding round due November 1 with decisions by mid-December and until May 1 to decide.', source: 'earlyAction' },
    ],

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Comprehensive fee (tuition, fees, housing and food)', amount: 92800 },
            { item: 'Books and supplies', amount: 1000 },
            { item: 'Miscellaneous', amount: 600 },
            { item: 'Transportation', amount: 400 },
        ],
        total: 94800,
        note: 'The comprehensive fee also covers special programs, some course-related travel and study away. Books, miscellaneous costs and transportation vary by student. Every student must also have health insurance, through family or the college\'s Student Health Insurance Plan.',
        source: 'costs',
        netPrice: {
            average: 35960,
            byIncome: [
                { income: '$0 to $30,000', amount: 17313 },
                { income: '$30,001 to $48,000', amount: 16600 },
                { income: '$48,001 to $75,000', amount: 22908 },
                { income: '$75,001 to $110,000', amount: 29863 },
                { income: 'Over $110,000', amount: 49170 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Conn\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
